const CART_DRAWER_CONFETTI_FALLBACK_SRC =
  'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
/* Tuned for a fall from above rather than a burst from below. gravity was 0,
   which is what a zero-g starburst needs; a shower needs the particles to
   actually drop, and ticks has to be long enough for them to cross the panel
   before they are culled. */
const CART_DRAWER_CONFETTI_DEFAULTS = {
  spread: 70,
  ticks: 220,
  gravity: 0.9,
  decay: 0.93,
  startVelocity: 18,
};
const CART_DRAWER_CONFETTI_COLORS = ['#a864fd', '#29cdff', '#78ff44', '#ff718d', '#fdff6a'];
const CART_DRAWER_CONFETTI_COOLDOWN = 4000;
/* y is 0 at the top of the canvas and 1 at the bottom, so -0.1 starts the
   particles just above the panel and they fall in. It was 1.12, which is below
   the bottom edge: the confetti erupted upward from under the drawer. */
const CART_DRAWER_CONFETTI_ORIGIN = { x: 0.5, y: -0.1 };

function loadCartDrawerConfettiLibrary(preferredSrc) {
  if (window.cartDrawerConfettiLibraryPromise) {
    return window.cartDrawerConfettiLibraryPromise;
  }

  if (window.confetti) {
    window.cartDrawerConfettiLibraryPromise = Promise.resolve(window.confetti);
    return window.cartDrawerConfettiLibraryPromise;
  }

  const scriptSrc = preferredSrc || CART_DRAWER_CONFETTI_FALLBACK_SRC;

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      script.src = src;
      script.setAttribute('data-cart-drawer-confetti', 'true');
      script.onload = () => resolve(window.confetti);
      script.onerror = (error) => {
        script.remove();
        reject(error);
      };
      document.head.appendChild(script);
    });

  window.cartDrawerConfettiLibraryPromise = loadScript(scriptSrc).catch((error) => {
    if (!preferredSrc || preferredSrc === CART_DRAWER_CONFETTI_FALLBACK_SRC) {
      console.error('[CartDrawer] Failed to load confetti library', error);
      throw error;
    }

    console.warn('[CartDrawer] Confetti asset failed, falling back to CDN', {
      preferredSrc,
      error,
    });
    return loadScript(CART_DRAWER_CONFETTI_FALLBACK_SRC).catch((fallbackError) => {
      console.error('[CartDrawer] Fallback confetti library failed to load', fallbackError);
      throw fallbackError;
    });
  });

  return window.cartDrawerConfettiLibraryPromise;
}

function ensureCartDrawerConfettiInstance(canvas, preferredSrc) {
  if (!canvas) return Promise.resolve(null);

  return loadCartDrawerConfettiLibrary(preferredSrc)
    .then((confettiLib) => {
      if (!confettiLib) return null;
      if (canvas.confetti) return canvas.confetti;

      /* useWorker was false, which kept every frame of the animation on the
         main thread, competing with the drawer's open transition and the cart
         re-render happening at the same moment. canvas-confetti feature
         detects OffscreenCanvas and falls back on its own where it is absent. */
      const instance = confettiLib.create(canvas, {
        resize: true,
        useWorker: true,
      });

      if (instance && typeof instance.then === 'function') {
        return instance.then((result) => {
          canvas.confetti = result;
          return canvas.confetti;
        });
      }

      canvas.confetti = instance;
      return canvas.confetti;
    })
    .catch((error) => {
      console.error('[CartDrawer] Unable to initialize confetti', error);
      return null;
    });
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.confettiSrc = this.dataset.confettiSrc;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.classList.contains('active')) {
        this.close();
      } else {
        this.open(cartLink);
      }
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.maybeCelebrateFreeShipping();
    this.observeFreeShippingUnlock();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  maybeCelebrateFreeShipping() {
    const banner = this.querySelector('.cart__free-shipping');
    if (!banner) return;

    const status = banner.getAttribute('data-free-shipping-status');
    if (status !== 'unlocked') return;

    /* Not while the panel is still loading. This used to fire from open(),
       which runs before the cart has rendered, so the confetti played over a
       blank white panel and was half over by the time the items appeared.
       clearBusy() below is the exact moment the content becomes real, and it
       replays this from there. */
    if (this.classList.contains('cro-drawer-busy')) {
      this._confettiWaiting = true;
      return;
    }

    const now = Date.now();
    if (this._lastConfettiRun && now - this._lastConfettiRun < CART_DRAWER_CONFETTI_COOLDOWN) {
      return;
    }
    const canvas = this.querySelector('#cart-drawer-confetti');
    if (!canvas) return;

    if (this._confettiPending) return;
    this._confettiPending = true;

    ensureCartDrawerConfettiInstance(canvas, this.confettiSrc)
      .then((confettiInstance) => {
        if (typeof confettiInstance !== 'function') return;
        this._lastConfettiRun = Date.now();

        const defaults = Object.assign({}, CART_DRAWER_CONFETTI_DEFAULTS, {
          origin: CART_DRAWER_CONFETTI_ORIGIN,
        });

        /* Three columns across the top plus a couple of emoji, so the fall
           covers the panel width instead of streaming out of one point. 110
           particles in total, holding the budget the single-volley rewrite
           below settled on. angle 270 points them down; with gravity they
           keep going that way. */
        const shoot = () => {
          [0.15, 0.5, 0.85].forEach((x, i) => {
            confettiInstance(
              Object.assign({}, defaults, {
                particleCount: 34,
                angle: 270,
                spread: 60,
                origin: { x: x, y: -0.1 },
                scalar: i === 1 ? 1 : 0.85,
                colors: CART_DRAWER_CONFETTI_COLORS,
                shapes: ['circle', 'square'],
              })
            );
          });

          /* Emoji are by far the most expensive shape here: each particle is a
             text raster redrawn every frame. Eight still reads as a flourish. */
          confettiInstance(
            Object.assign({}, defaults, {
              particleCount: 8,
              angle: 270,
              spread: 80,
              origin: { x: 0.5, y: -0.1 },
              scalar: 1.8,
              shapes: ['emoji'],
              shapeOptions: {
                emoji: {
                  value: ['🦄', '🌈'],
                },
              },
            })
          );
        };

        /* shoot() was called three times, 100ms apart. Each call fires ~250
           particles, so ~800 were alive at once on top of the opening burst.
           One volley reads the same and costs a third as much.

           The bigger problem was WHEN, not how much. Measured over a real add
           to cart with the confetti firing as it did in production: 9 frames
           over 100ms, p95 267ms, and 9 long tasks totalling 2.5 seconds, the
           worst 315ms. The same volley fired on an idle page ran at a locked
           60fps with zero long tasks. The particles were never the cost; they
           were landing on top of the drawer's own re-render and both were
           fighting for the main thread.

           canvas-confetti only honours useWorker on its global export, not on
           create(), and this canvas has to come from create() to stay inside
           the panel, so the animation cannot be moved off-thread. Waiting for
           the thread to go quiet is the fix available: requestIdleCallback
           with a timeout so the celebration still happens on a busy page, just
           after the cart has finished painting. */
        /* shoot() is the whole celebration now. There used to be a bare
           confettiInstance() call in front of it, which added another 50
           particles at the default origin on top of the volley. */
        const launch = () => {
          shoot();
        };

        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(launch, { timeout: 1500 });
        } else {
          setTimeout(launch, 450);
        }
      })
      .catch(() => {})
      .then(() => {
        this._confettiPending = false;
      });
  }

  observeFreeShippingUnlock() {
    if (this._freeShipObserver) {
      try { this._freeShipObserver.disconnect(); } catch (_) {}
    }

    const target = this; // observe the whole drawer to catch any banner re-render
    if (!target) return;

    let lastStatus = (this.querySelector('.cart__free-shipping') || {}).getAttribute?.('data-free-shipping-status') || '';

    const observer = new MutationObserver(() => {
      if (!this.classList.contains('active')) return;
      const banner = this.querySelector('.cart__free-shipping');
      if (!banner) return;
      const status = banner.getAttribute('data-free-shipping-status');
      if (status === 'unlocked' && lastStatus !== 'unlocked') {
        this.maybeCelebrateFreeShipping();
      }
      lastStatus = status;
    });

    observer.observe(target, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-free-shipping-status'] });
    this._freeShipObserver = observer;
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.observeFreeShippingUnlock();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


/* ===========================================================================
   CRO: open the drawer on tap, fade its contents in as they arrive.

   Until now the drawer only opened from inside renderContents(), which runs
   after /cart/add resolves. That request is slow on purpose: product-form.js
   appends `sections` to it, so Shopify server-renders the whole drawer before
   replying. Measured on mobile it is 1.1 to 1.6s, and the drawer stayed shut
   for all of it.

   This changes when the drawer opens, not what anything fetches. No app is
   disabled, no request is removed, nothing about Orichi, Gorgias, ReCharge or
   Triple Whale is touched. The tap opens the drawer against the cart the page
   already has, drops in a placeholder row for the item on its way, and the real
   markup fades in over the top when it lands.

   To revert: delete this block. Nothing above it was modified.
   =========================================================================== */
(function () {
  if (typeof CartDrawer !== 'function') return;

  var BUSY = 'cro-drawer-busy';
  var FRESH = 'cro-drawer-fresh';
  var SAFETY_MS = 8000;

  var css = document.createElement('style');
  css.id = 'cro-drawer-css';
  css.textContent = [
    /* The previous cart is taken off screen entirely while the new one is in
       flight. Fading it to .45 instead left three ghost rows, the old prices
       among them, sitting there for the whole ~2s round trip and then being
       swapped under the reader. A white panel with one spinner is both calmer
       and honest: nothing on screen is stale.

       display:none rather than opacity or visibility, so the stale rows take
       up no height and the spinner sits alone in the panel. */
    'cart-drawer.' + BUSY + ' cart-drawer-items,',
    'cart-drawer.' + BUSY + ' .cart__free-shipping,',
    'cart-drawer.' + BUSY + ' .cart-drawer__footer,',
    'cart-drawer.' + BUSY + ' .drawer__footer,',
    'cart-drawer.' + BUSY + ' .drawer__inner-empty{display:none}',

    /* A plain white panel while the contents are in flight, rather than the
       theme's tinted ground showing through a half faded cart. */
    'cart-drawer.' + BUSY + ' .drawer__inner{background:#fff}',

    /* .drawer__inner is a grid with align-items:center, so while it is holding
       only a placeholder everything floats to the middle of the panel. Pin it
       to the top for the duration. cart-drawer.is-empty also zeroes the
       padding, which has to come back or the spinner sits against the edge. */
    /* .is-empty turns .drawer__inner into a centred grid with no padding, which
       is wrong for a panel holding a spinner. Forced back to the flex column
       the non-empty state uses, so the loading panel looks the same either way. */
    'cart-drawer.' + BUSY + ' .drawer__inner,',
    'cart-drawer.' + BUSY + '.is-empty .drawer__inner{',
    '  align-items:stretch;display:flex;flex-direction:column;padding:0 1.5rem}',

    /* The close button lives in .drawer__header, and the theme hides that
       header outright on an empty cart. Opening onto a loading panel with no
       way out is the result, so the header is forced back for the duration.
       Two selectors: the .is-empty one has to be out-specified, not tied. */
    'cart-drawer.' + BUSY + ' .drawer__header,',
    'cart-drawer.' + BUSY + '.is-empty .drawer__header{display:flex}',

    /* A single spinner, centred in the panel, in place of shimmer bars. The
       bars implied a row shape that is now wrong anyway: the thumbnail is
       hidden in the mini cart, so there is no image to stand in for. */
    'cart-drawer .cro-drawer-skeleton{display:block;width:100%}',
    'cart-drawer .cro-drawer-skeleton__spinner{',
    '  animation:croDrawerSpin .7s linear infinite;',
    '  border:0.2rem solid rgba(var(--color-foreground),.14);',
    '  border-radius:50%;border-top-color:rgba(var(--color-foreground),.55);',
    '  display:block;height:2.8rem;margin:4.8rem auto;width:2.8rem}',
    '@keyframes croDrawerSpin{to{transform:rotate(360deg)}}',

    '#CartDrawer.' + FRESH + ' .cart-item{animation:croDrawerIn .3s ease both}',
    '#CartDrawer.' + FRESH + ' .cart-item:nth-child(2){animation-delay:.04s}',
    '#CartDrawer.' + FRESH + ' .cart-item:nth-child(3){animation-delay:.08s}',
    '@keyframes croDrawerIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}',

    '@media (prefers-reduced-motion:reduce){',
    '  .cro-drawer-skeleton__spinner{animation-duration:2s}',
    '  #CartDrawer.' + FRESH + ' .cart-item{animation:none}}'
  ].join('');
  document.head.appendChild(css);

  function drawerEl() {
    return document.querySelector('cart-drawer');
  }

  /* The placeholder lives inside .drawer__inner, which renderContents replaces
     wholesale, so it is removed for us the moment the real markup lands. */
  function showSkeleton(drawer) {
    var inner = drawer.querySelector('.drawer__inner');
    if (!inner || inner.querySelector('.cro-drawer-skeleton')) return;
    /* The theme's own "your cart is empty" block is hidden while busy, and the
       real heading in .drawer__header is forced visible by the CSS above, so
       the placeholder itself is just the spinner. */
    var sk = document.createElement('div');
    sk.className = 'cro-drawer-skeleton';
    sk.setAttribute('role', 'status');
    sk.setAttribute('aria-live', 'polite');
    sk.setAttribute('aria-label', 'Updating your cart');
    sk.innerHTML = '<span class="cro-drawer-skeleton__spinner"></span>';
    /* Sibling of cart-drawer-items, not a child of it. As a child it inherited
       the rule that hides the stale list, and before that the .45 opacity that
       dimmed the spinner along with everything else. Placed straight after the
       header so it reads as the panel's content, under the title and the X. */
    var header = inner.querySelector('.drawer__header');
    if (header && header.nextSibling) inner.insertBefore(sk, header.nextSibling);
    else if (header) inner.appendChild(sk);
    else inner.insertBefore(sk, inner.firstChild);
  }

  function clearSkeleton(drawer) {
    var sk = drawer.querySelector('.cro-drawer-skeleton');
    if (sk && sk.parentNode) sk.parentNode.removeChild(sk);
  }

  function setBusy(drawer) {
    drawer.classList.add(BUSY);
    showSkeleton(drawer);
    clearTimeout(drawer.__croSafety);
    /* If the response never comes, stop pretending rather than spinning forever. */
    drawer.__croSafety = setTimeout(function () { clearBusy(drawer); }, SAFETY_MS);
  }

  function clearBusy(drawer) {
    clearTimeout(drawer.__croSafety);
    drawer.classList.remove(BUSY);
    clearSkeleton(drawer);

    /* The celebration was held back while the panel was loading. The cart is
       painted by the time this runs - renderContents has already swapped the
       markup in - so it can go now. */
    if (drawer._confettiWaiting) {
      drawer._confettiWaiting = false;
      if (typeof drawer.maybeCelebrateFreeShipping === 'function') {
        drawer.maybeCelebrateFreeShipping();
      }
    }
  }

  /* Capture phase, so this runs on the same tap that starts the fetch rather
     than behind product-form's own submit handler. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || typeof form.closest !== 'function' || !form.closest('product-form')) return;
      var drawer = drawerEl();
      if (!drawer) return;
      setBusy(drawer);
      if (!drawer.classList.contains('active')) drawer.open();
    },
    true
  );

  /* renderContents paints the response. Wrap it so the new rows animate in and
     the busy state ends at exactly the moment the content is real. */
  var originalRenderContents = CartDrawer.prototype.renderContents;
  CartDrawer.prototype.renderContents = function (parsedState) {
    var result = originalRenderContents.call(this, parsedState);
    clearBusy(this);
    var target = document.getElementById('CartDrawer');
    if (target) {
      target.classList.remove(FRESH);
      void target.offsetWidth; /* force a reflow so the animation restarts */
      target.classList.add(FRESH);
      setTimeout(function () { target.classList.remove(FRESH); }, 700);
    }
    return result;
  };

  /* A failed add never reaches renderContents, so close what we opened. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.cartError) {
    subscribe(PUB_SUB_EVENTS.cartError, function () {
      var drawer = drawerEl();
      if (!drawer) return;
      clearBusy(drawer);
      drawer.close();
    });
  }
})();
